import { SquareArrowOutDownRightIcon } from "lucide-react";
import { FC, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "~/components/ui/button";
import { Checkbox } from "~/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { EXPORT_TYPE, REPORT_EXPORT_TYPE } from "../../constants";

type ExportOption = {
  type: REPORT_EXPORT_TYPE | EXPORT_TYPE;
  label: string;
};

const DOCLIST_FORMATS = new Set<REPORT_EXPORT_TYPE | EXPORT_TYPE>([
  REPORT_EXPORT_TYPE.ASYNC_PDF,
  REPORT_EXPORT_TYPE.ASYNC_DOCX,
]);

interface ReportExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  options: ExportOption[];
  hasDoclist: boolean;
  onExport: (
    type: REPORT_EXPORT_TYPE | EXPORT_TYPE,
    options?: { hideReferences?: boolean }
  ) => void;
  isExporting?: boolean;
}

export const ReportExportModal: FC<ReportExportModalProps> = ({
  isOpen,
  onClose,
  options,
  hasDoclist,
  onExport,
  isExporting = false,
}) => {
  const intl = useIntl();
  const [selected, setSelected] = useState<
    REPORT_EXPORT_TYPE | EXPORT_TYPE | undefined
  >(options[0]?.type);
  const [includeDoclist, setIncludeDoclist] = useState(false);
  const [showReferences, setShowReferences] = useState(true);

  useEffect(() => {
    if (isOpen) {
      setSelected(options[0]?.type);
      setIncludeDoclist(false);
      setShowReferences(true);
    }
  }, [isOpen, options]);

  const showDoclistOption =
    hasDoclist && selected !== undefined && DOCLIST_FORMATS.has(selected);

  const handleExport = () => {
    if (!selected) return;
    if (includeDoclist && selected === REPORT_EXPORT_TYPE.ASYNC_PDF) {
      onExport(REPORT_EXPORT_TYPE.ASYNC_PDF_WITH_DOCLIST, {
        hideReferences: !showReferences,
      });
    } else if (includeDoclist && selected === REPORT_EXPORT_TYPE.ASYNC_DOCX) {
      onExport(REPORT_EXPORT_TYPE.ASYNC_DOCX_WITH_DOCLIST, {
        hideReferences: !showReferences,
      });
    } else {
      onExport(selected, { hideReferences: !showReferences });
    }
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="w-full max-w-sm">
        <DialogHeader>
          <DialogTitle>
            <FormattedMessage
              id="report.export.modal.title"
              defaultMessage="Export Report"
            />
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-3 py-1">
          <span className="text-sm text-muted-foreground shrink-0">
            <FormattedMessage
              id="report.export.modal.exportAs"
              defaultMessage="Export as"
            />
          </span>
          <Select
            value={selected}
            onValueChange={(v) => {
              const val = v as REPORT_EXPORT_TYPE | EXPORT_TYPE;
              setSelected(val);
              if (!DOCLIST_FORMATS.has(val)) {
                setIncludeDoclist(false);
              }
            }}
          >
            <SelectTrigger className="flex-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {options.map((item) => (
                <SelectItem key={item.type} value={item.type}>
                  {intl.formatMessage({ id: item.label + ".type" })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {showDoclistOption && (
          <>
            <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-border px-3 py-2.5 hover:border-muted-foreground/40 transition-colors">
              <Checkbox
                checked={includeDoclist}
                onCheckedChange={(v) => {
                  setIncludeDoclist(!!v);
                  if (!v) setShowReferences(true);
                }}
                className="mt-0.5"
              />
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-medium leading-none">
                  <FormattedMessage
                    id="report.export.includeDoclist"
                    defaultMessage="Include original documents"
                  />
                </span>
                <span className="text-xs text-muted-foreground">
                  <FormattedMessage
                    id="report.export.includeDoclist.description"
                    defaultMessage="Append the full document list with page images"
                  />
                </span>
              </div>
            </label>

            <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-border px-3 py-2.5 hover:border-muted-foreground/40 transition-colors">
              <Checkbox
                checked={showReferences}
                onCheckedChange={(v) => setShowReferences(!!v)}
                className="mt-0.5"
              />
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-medium leading-none">
                  <FormattedMessage
                    id="report.export.showReferences"
                    defaultMessage="Show References"
                  />
                </span>
                <span className="text-xs text-muted-foreground">
                  <FormattedMessage
                    id="report.export.showReferences.description"
                    defaultMessage="Include inline citation references"
                  />
                </span>
              </div>
            </label>
          </>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={isExporting}>
            <FormattedMessage id="cancel" defaultMessage="Cancel" />
          </Button>
          <Button onClick={handleExport} disabled={!selected || isExporting}>
            <SquareArrowOutDownRightIcon className="w-4 h-4 mr-2" />
            <FormattedMessage id="common.export" defaultMessage="Export" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
